import { AntDropdown, defaultTheme, Icons, Text } from '@agro-club/agroclub-shared'
import { PAGE_SIZE_1K } from 'modules/constants'
import { useTableData } from 'modules/domain/common/hooks'
import { Company } from 'modules/domain/company/types'
import { ListRequestParams } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'
import styled from 'styled-components'
import { AButton, ARouterLink } from 'views/components/Analytics'
import { defaultListRequestParams } from 'views/components/TableFilters/data'
import CompanyRoutes from 'views/pages/Company/routes'

interface Props {
  company: Company
  onLoaded: (hasRelatedCompanies: boolean) => void
}

const StyledCompanyText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  svg {
    width: 16px;
    height: 16px;

    path {
      fill: ${defaultTheme.color.primary600};
    }
  }
`

const СompanyEntry = ({ company, currentCompanyId }: { company: Company; currentCompanyId: number | string }) => {
  return (
    <StyledCompanyText>
      <ARouterLink eventName="company" itemId={company.id} to={generatePath(CompanyRoutes.Details, { id: company.id })}>
        <Text typography="bodyMedium">{company.name} </Text>
      </ARouterLink>
      {company.id.toString() === currentCompanyId.toString() && <Icons.IconDone />}
    </StyledCompanyText>
  )
}

export const CompaniesInfo = ({ company, onLoaded }: Props) => {
  const { t } = useTranslation('user')
  const [listRequestParams] = useState<ListRequestParams>({ ...defaultListRequestParams, pageSize: PAGE_SIZE_1K })
  const parentId = company.parent?.id || company.id
  const { data } = useTableData<Company>(endpoints.companyBranches(parentId), listRequestParams, true)
  const groupName = company.parent?.companies_group_name || company.companies_group_name
  let title = company.name
  if (groupName) {
    title = `${title} (${groupName})`
  }

  useEffect(() => {
    if (!data) return
    onLoaded(data.length > 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const items = useMemo(() => {
    if (!data || data.length < 2) {
      return []
    }
    const additionalCompanies: Company[] = []
    let mainCompany: Company | undefined
    data.forEach((branch) => {
      if (branch.parent) {
        additionalCompanies.push(branch)
      } else {
        mainCompany = branch
      }
    })
    if (!mainCompany || !additionalCompanies.length) return []
    return [
      {
        key: 'main',
        type: 'group',
        label: t('mainCompany'),
        children: [{ key: 'main-1', label: <СompanyEntry currentCompanyId={company.id} company={mainCompany} /> }],
      },
      {
        key: 'additional',
        type: 'group',
        label: t('additionalCompanies'),
        children: additionalCompanies.map((c) => ({
          key: c.id,
          label: <СompanyEntry key={c.id} currentCompanyId={company.id} company={c} />,
        })),
      },
    ]
  }, [company.id, data, t])

  if (!data) {
    return null
  }
  if (data.length === 1) {
    return (
      <ARouterLink eventName="company" itemId={company.id} to={generatePath(CompanyRoutes.Details, { id: company.id })}>
        {company.name}
      </ARouterLink>
    )
  }

  return (
    <>
      <AntDropdown
        triggerElement={
          <AButton id="companyInfo" size="small" color="secondary">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {title} <Icons.IconArrowDownS />
            </div>
          </AButton>
        }
        items={items}
      />
    </>
  )
}
